/**
 * Язык приложения: контекст, переключатель и функция перевода.
 *
 * Язык уезжает и на сервер — параметром `lang`: тексты акций переводит
 * партнёр, и подставляет их сервер. Иначе у одной акции было бы два перевода.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { translate } from './dict';
import { setLocale } from './locale';
import { initialLang, storeLang, type Lang } from './lang';

interface State {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /**
   * t('Акции') — перевод или та же строка, если перевода нет.
   *
   * Второй аргумент подставляет значения в места вида {имя}: фраза с числом
   * внутри переводится целиком, иначе в английском получается русский порядок
   * слов, склеенный из кусков.
   */
  t: (text: string, vars?: Record<string, string | number>) => string;
}

const Ctx = createContext<State | null>(null);

export function LangProvider({
  children,
  onChange,
}: {
  children: ReactNode;
  /** Сообщить клиенту API, на каком языке просить контент. */
  onChange?: (lang: Lang) => void;
}) {
  const [lang, setLangState] = useState<Lang>(() => {
    const initial = initialLang();
    onChange?.(initial);
    return initial;
  });

  const setLang = useCallback(
    (next: Lang) => {
      setLangState(next);
      storeLang(next);
      onChange?.(next);
      // Язык влияет на то, что присылает сервер, поэтому меняем и адрес:
      // ссылкой на английскую версию можно поделиться.
      try {
        const url = new URL(window.location.href);
        if (next === 'ru') url.searchParams.delete('lang');
        else url.searchParams.set('lang', next);
        window.history.replaceState({}, '', url);
      } catch {
        /* ignore */
      }
    },
    [onChange],
  );

  // lang в <html> — не украшение: по нему браузер переносит слова, а робот
  // понимает, на каком языке страница. В разметке он задан разом на всех, а
  // выбирается язык уже в браузере.
  useEffect(() => {
    document.documentElement.lang = lang;
    setLocale(lang);
  }, [lang]);

  const value = useMemo<State>(
    () => ({
      lang,
      setLang,
      t: (text: string, vars?: Record<string, string | number>) =>
        fill(translate(lang, text), vars),
    }),
    [lang, setLang],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLang(): State {
  const ctx = useContext(Ctx);
  // Без провайдера возвращаем русский и перевод-пустышку: компонент из
  // пакета ui не обязан знать, подключён ли язык в этом приложении.
  return ctx ?? { lang: 'ru', setLang: () => {}, t: (text, vars) => fill(text, vars) };
}

/** Короткий доступ к переводу. */
export function useT(): (text: string, vars?: Record<string, string | number>) => string {
  return useLang().t;
}

/** Подстановка значений в места вида {имя}. */
function fill(text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in vars ? String(vars[key]) : whole,
  );
}

/**
 * Множественное число с переводом.
 *
 * В русском три формы и выбирает их число, в английском и испанском — две и
 * выбирает их единица. Поэтому в словаре у такой строки ключ это все три
 * русские формы через «|», а перевод — две: `'приз|приза|призов':
 * 'prize|prizes'`. Непереведённая строка даёт русские формы, а не «приз(ов)».
 */
export function usePlural(): (n: number, one: string, few: string, many: string) => string {
  const { lang, t } = useLang();
  return (n, one, few, many) => {
    // Английский и испанский устроены одинаково: две формы, выбирает единица.
    if (lang !== 'ru') {
      const forms = t(`${one}|${few}|${many}`).split('|');
      if (forms.length === 2) return n === 1 ? (forms[0] as string) : (forms[1] as string);
    }
    const abs = Math.abs(n);
    const d = abs % 10;
    const h = abs % 100;
    if (d === 1 && h !== 11) return one;
    if (d >= 2 && d <= 4 && (h < 10 || h >= 20)) return few;
    return many;
  };
}
